import { inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { enduserInterface } from 'src/app/util/interfaces/enduser';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthLoginService } from 'src/app/page/auth-page/service/auth-login.service';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { localeInterface } from 'src/app/util/interfaces/locale';
import { styleSheetInterface } from 'src/app/util/interfaces/styleSheet';
import { customerInterface } from 'src/app/util/interfaces/customer';
import { DateFormat, DateTimeFormat } from '../date/date.service';

@Injectable({
	providedIn: 'root',
})
export class ProfileService {
	private http = inject(HttpClient);
	private AuthLoginService = inject(AuthLoginService);

	private meUrl = environment.baseApiUrl + '{userType}/me';
	private localeUrl = environment.baseApiUrl + 'tool/locale';
	private getStyleSheetListUrl = environment.baseApiUrl + 'customer/stylesheet?app_source=msp';
	private getStyleSheetDataUrl = environment.baseApiUrl + 'customer/stylesheet/{idStyleSheet}';
	private getAvailableCustomersUrl = environment.baseApiUrl + '{userType}/customers';
	private enduserContactsUrl = environment.baseApiUrl + 'enduser/contacts';
	private updateEnduserEmailLogUrl = environment.baseApiUrl + 'enduser/enduserEmailLog';
	private updateAnalystEmailLogUrl = environment.baseApiUrl + 'analyst/analystEmailLog';
	private updateAnalystShowAwaitingAuthorizationUrl = environment.baseApiUrl + 'analyst/awaitingAuthorization';

	public styleSheetList: styleSheetInterface[];
	public userStyleSheet: styleSheetInterface;
	public availableCustomers: customerInterface[];
	public userData: WritableSignal<enduserInterface> = signal(null);

	constructor() {}

	headers = new HttpHeaders({ Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken() });

	setLocalUser(): Observable<enduserInterface> {
		const meUrl = this.meUrl.replace('{userType}', localStorage.getItem('sessionStatus') === '1' ? 'analyst' : 'enduser');
		return this.http.get<JSON>(meUrl, { headers: this.headers }).pipe(
			map((userData: any) => {
				const newUser: enduserInterface = {
					idCustomer: userData.idcust,
					customer: userData.customer,
					department: userData.customer,
					solution_area_default: userData.solution_area_default,
					email: userData.email,
					fullName: userData.fullName,
					firstName: userData.fullName.split(' ')[0],
					firstNameLetters: this.getEnduserInitialsName(userData.fullName),
					location: userData.location,
					login: userData.login,
					last_password: userData.last_password,
					phone: userData.phone,
					mobile: userData.mobile,
					site: userData.site,
					uniquekey: userData.uniquekey,
					usertype: userData.usertype,
					vip: userData.vip ? userData.vip : false,
					id: userData.id,
					idAnalyst: userData.idAnalyst,
					css_pattern: userData.css_pattern,
					gmt: userData.gmt,
					locale: userData.locale,
					language: userData.language,
					contacts: userData.contacts,
					permission: userData.permission,
					sessionStatus: userData.sessionStatus, // Se BehalfOf está ativo, seta sessão como enduser para não desconectar automático caso não exista sessionStatus no usuário
					main_menu: userData.main_menu,
					availableCustomers: userData.availableCustomers,
					availableSolutionAreas: userData.availableSolutionAreas,
					enduser_active: userData.enduser_active,
					analyst_active: userData.analyst_active,
					sourceIdBehalfOf: userData.sourceIdBehalfOf,
					sourceFullNameBehalfOf: userData.sourceFullNameBehalfOf,
					...(localStorage.getItem('sessionStatus') == '1' && {
						showAwaitingAuthorization: userData.showAwaitingAuthorization,
					}),
				};

				this.userData.set(newUser);
				return this.userData();
			})
		);
	}

	getUpdateUserBody() {
		return {
			language: this.userData().language,
			location: this.userData().location,
			idcust: this.userData().idCustomer,
			idCustomer: this.userData().idCustomer,
			site: this.userData().site,
			last_access: Date.now(),
			phone: this.userData().phone,
			uniquekey: this.userData().uniquekey,
			email: this.userData().email,
			gmt: this.userData().gmt,
			last_password: this.userData().last_password,
			locale: this.userData().locale,
			solution_area_default: this.userData().solution_area_default,
			usertype: this.userData().usertype,
			customer: this.userData().customer,
			css_pattern: this.userData().css_pattern.toString(),
			login: this.userData().login,
			id: this.userData().id,
			fullName: this.userData().fullName,
			mobile: this.userData().mobile,
			sessionStatus: this.userData().sessionStatus,
			endusers_contacts: [],
		};
	}

	updateUser(body: any): Observable<boolean> {
		const meUrl = this.meUrl.replace('{userType}', localStorage.getItem('sessionStatus') === '1' ? 'analyst' : 'enduser');
		return this.http.put<JSON[]>(meUrl, body, { headers: this.headers }).pipe(
			map(() => {
				return true;
			})
		);
	}

	getLocales(): Observable<localeInterface[]> {
		return this.http.get<JSON[]>(this.localeUrl, { headers: this.headers }).pipe(
			map((localeArray) => {
				const newLocaleArray: localeInterface[] = [];
				if (localeArray) {
					localeArray.forEach((locale: any) => {
						const newLocale: localeInterface = {
							ID: locale.Id,
							LOCALE: locale.Locale,
							GMT: locale.GMT,
						};
						newLocaleArray.push(newLocale);
					});
				}
				return newLocaleArray;
			})
		);
	}

	getStyleSheetList(onlyCustomerCreated: boolean): Observable<styleSheetInterface[]> {
		const getStyleSheetListUrl = this.getStyleSheetListUrl + `${onlyCustomerCreated == true ? '&customOnly=true' : ''}`;
		return this.http.get<any>(getStyleSheetListUrl, { headers: this.headers }).pipe(
			map((styleSheetListRes) => {
				const newStyleSheetArray: styleSheetInterface[] = [];
				for (const styleSheetRes of styleSheetListRes) {
					const newStyleSheet: styleSheetInterface = {
						id: styleSheetRes.id,
						name: styleSheetRes.name,
						default: styleSheetRes.default,
						app_source: styleSheetRes.app_source,
						'primary-color-1': styleSheetRes['primary-color-1'] || undefined,
						'primary-color-1-dark-mode': styleSheetRes['primary-color-1-dark-mode'] || undefined,
						'primary-color-2': styleSheetRes['primary-color-2'] || undefined,
						'primary-color-2-dark-mode': styleSheetRes['primary-color-2-dark-mode'] || undefined,
					};

					newStyleSheetArray.push(newStyleSheet);
				}

				return newStyleSheetArray;
			})
		);
	}

	setStyleSheetList(stylesheet: styleSheetInterface[]): void {
		this.styleSheetList = stylesheet;
	}

	getUserStylesheet(): styleSheetInterface {
		return this.userStyleSheet;
	}

	setUserStylesheet(styleSheet: styleSheetInterface): void {
		this.userStyleSheet = styleSheet;
	}

	getStyleSheetData(idStyleSheet: string): Observable<styleSheetInterface> {
		const getStyleSheetDataUrl = this.getStyleSheetDataUrl.replace('{idStyleSheet}', idStyleSheet);
		return this.http.get<any>(getStyleSheetDataUrl, { headers: this.headers }).pipe(
			map((styleSheetRes) => {
				const newStyleSheet: styleSheetInterface = {
					id: styleSheetRes.id,
					name: styleSheetRes.name,
					default: styleSheetRes.default,
					'primary-color-1': styleSheetRes['primary-color-1'],
					'primary-color-1-dark-mode': styleSheetRes['primary-color-1-dark-mode'],
					'primary-color-2': styleSheetRes['primary-color-2'],
					'primary-color-2-dark-mode': styleSheetRes['primary-color-2-dark-mode'],
					app_source: styleSheetRes.app_source,
					logo: {
						content: '',
						extension: '',
						file_path: '',
						mimeType: '',
						name: '',
						size: 0,
					},
				};

				return newStyleSheet;
			})
		);
	}

	setAvailableCustomers(): Observable<customerInterface[]> {
		const getAvailableCustomersUrl = this.getAvailableCustomersUrl.replace(
			'{userType}',
			localStorage.getItem('sessionStatus') === '1' ? 'analyst' : 'enduser'
		);
		return this.http.get<any[]>(getAvailableCustomersUrl, { headers: this.headers }).pipe(
			map((customerRes) => {
				const customerArray: customerInterface[] = [];
				for (const customer of customerRes) {
					const newCustomer: customerInterface = {
						id: customer.id,
						customer: customer.name,
					};
					customerArray.push(newCustomer);
				}
				this.availableCustomers = customerArray;

				return customerArray;
			})
		);
	}

	getAvailableCustomers(): Observable<customerInterface[]> {
		return this.availableCustomers == undefined
			? this.setAvailableCustomers()
			: new Observable((observer) => {
					observer.next(this.availableCustomers);
					observer.complete();
				});
	}

	postEnduserContacts(enduserContactsBody: any): Observable<any> {
		return this.http.post<any>(this.enduserContactsUrl, enduserContactsBody, { headers: this.headers });
	}

	putEnduserContacts(enduserContactsBody: any): Observable<any> {
		return this.http.put<any>(this.enduserContactsUrl, enduserContactsBody, { headers: this.headers });
	}

	deleteEnduserContacts(enduserContactsBody: any): Observable<any> {
		return this.http.request('delete', this.enduserContactsUrl, {
			headers: this.headers,
			body: enduserContactsBody,
		});
	}

	putEnduserEmailsLog(data: any): Observable<any> {
		const body = { receiveEmailsLog: data };

		return this.http.put(this.updateEnduserEmailLogUrl, body, { headers: this.headers });
	}

	putAnalystEmailsLog(data: any): Observable<any> {
		const body = { receiveEmailsLog: data };

		return this.http.put(this.updateAnalystEmailLogUrl, body, { headers: this.headers });
	}

	putAnalystShowAwaitingAuthorization(data: any): Observable<any> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});

		return this.http.put<any>(
			this.updateAnalystShowAwaitingAuthorizationUrl,
			{ showAwaitingAuthorization: data },
			{ headers: headers }
		);
	}

	getDateTimeFullFormatLabel(): string {
		let dateFormat: any = localStorage.getItem('dateFormat');
		const allowedDateFormat = ['YMD HMS', 'DMY HMS', 'MDY HMS'];
		if (!allowedDateFormat.includes(dateFormat)) {
			localStorage.setItem('dateFormat', 'YMD HMS');
			dateFormat = 'YMD HMS';
		}

		switch (dateFormat) {
			case 'YMD HMS': {
				return 'lbl_formato_data_aaaa_mm_dd_hh_mm';
			}

			case 'DMY HMS': {
				return 'lbl_formato_data_dd_mm_aaaa_hh_mm';
			}

			case 'MDY HMS': {
				return 'lbl_formato_data_mm_dd_aaaa_hh_mm';
			}
		}
	}

	getDateFormat(): DateFormat {
		let dateFormat: string = localStorage.getItem('dateFormat');
		const allowedDateFormat = ['YMD HMS', 'DMY HMS', 'MDY HMS'];
		if (!allowedDateFormat.includes(dateFormat)) {
			localStorage.setItem('dateFormat', 'YMD HMS');
			dateFormat = 'YMD HMS';
		}
		if (dateFormat.split(' ').length > 1) return dateFormat.split(' ')[0] as DateFormat;
		return dateFormat as DateFormat;
	}

	getDateTimeFormat(): DateTimeFormat {
		//Por enquanto os formatos permitidos apenas tem HMS, mas a implementação fica pronta
		//para quando for utilizada outros valores
		let dateFormat: string = localStorage.getItem('dateFormat');
		const allowedDateFormat = ['YMD HMS', 'DMY HMS', 'MDY HMS'];
		if (!allowedDateFormat.includes(dateFormat)) {
			localStorage.setItem('dateFormat', 'YMD HMS');
			dateFormat = 'YMD HMS';
		}
		if (dateFormat.split(' ').length > 1) return dateFormat.split(' ')[1] as DateTimeFormat;
		return dateFormat as DateTimeFormat;
	}

	getEnduserInitialsName(fullName: string): string {
		if (fullName != undefined) {
			try {
				const trimSpacesRegex = / +(?= )/g;
				fullName = fullName.replaceAll(trimSpacesRegex, '');
				const nameSplited: string[] = fullName.trim().split(' ');
				let nameInitials = nameSplited[0][0].toUpperCase();
				nameInitials += nameSplited.length > 1 ? nameSplited[1][0].toUpperCase() : nameSplited[0][1].toUpperCase();

				return nameInitials;
			} catch (error) {
				console.error('Error getting enduser initials name:', error);
				return '';
			}
		}
		return '';
	}
}
